import { type JSONSchema7 } from 'json-schema';
import {
  AWARD_POINTS,
  AWARD_SCORES,
  RANDOMIZE_POLL_ALL,
  RANDOMIZE_POLL_GROUPS,
  RANDOMIZE_POLL_SETS,
} from '../../constants';
import { exclusivePrototypeAnswerSchema } from '../kinds/poll/questions/PrototypeScreen/PrototypeScreen.schema';
import { systemButtonText } from '../kinds/poll/questions/questionSchemaFields';
import {
  instantStatisticRequired,
  questionHasTimer,
  questionRandomizeGroup,
  questionSequentialDisplay,
  setRandomizePages,
  topLevelLogicRules,
} from '../kinds/poll/questions/schemaFields';
import { extractMixins, SchemaWithDefinitions } from '../mixins';
import { PIXEL_EVENTS } from '../pixel';
import { createTheme } from '../themes/';
import { createMockedPollTheme } from '../themes/createThemeSchema';
import i18nSchema from './i18n.schema.json';

type ListToDescriptions<
  L extends readonly { config: { name: string }; schema: unknown }[],
  Acc extends unknown[] = [],
> = L extends readonly [infer H, ...infer Rest]
  ? H extends { config: any; schema: unknown }
    ? Rest extends { config: { name: string }; schema: unknown }[]
      ? ListToDescriptions<Rest, [...Acc, { name: H['config']['name']; schema: H['schema'] }]>
      : Acc
    : Acc
  : Acc;

export function listToDescription<T extends readonly { config: { name: string }; schema: unknown }[]>(
  list: T,
): ListToDescriptions<T> {
  return list.map(v => ({
    name: v.config.name,
    schema: v.schema,
  })) as unknown as ListToDescriptions<T>;
}

export function createRootSchema<D extends object, R extends readonly string[], P extends Record<string, JSONSchema7>>(
  properties: P,
  required: R,
  definitions: D,
) {
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      env: { enum: ['prod'] },
      lang: { enum: ['en_US', 'ky_KG', 'ru_RU', 'tg_TJ', 'uz_UZ'] },
      wave: { type: 'number' },
      revision: { type: 'number' },
      firstId: { type: 'string' },
      template: { enum: ['poll', 'dialog', 'dialog_webview', 'webview'] },
      awardType: { enum: [AWARD_POINTS, AWARD_SCORES] },
      isCompleted: { type: 'boolean' },
      isCompletedAtTime: { type: 'string' },
      pixels: { type: 'array', items: { $ref: '#/definitions/Pixel' } },
      minHeight: { type: 'number' },
      alignSurvey: { enum: ['left', 'center'] },
      maxWidth: { type: 'number' },
      minWidth: { type: 'number' },
      hideIframeBorder: { type: 'boolean' },
      theme: { $ref: '#/definitions/Theme' },
      textFieldDisclaimer: { type: 'string' },
      useSinglePass: { type: 'boolean' },
      useServerPass: { type: 'boolean' },
      randomizePoll: { enum: [RANDOMIZE_POLL_GROUPS, RANDOMIZE_POLL_ALL, RANDOMIZE_POLL_SETS] },
      randomizePageLimit: { type: 'number' },
      randomizePollExcludedIds: {
        items: { type: 'string' },
        type: 'array',
      },
      singlePassExpiry: { type: ['number', 'null'] },
      templateId: { type: 'string' },
      contactPointId: { type: 'string' },
      useStrict: { type: 'boolean' },
      publicKey: { type: 'string' },
      useE2EEncryption: { type: 'boolean' },
      hasShare: { type: 'boolean' },
      restoreState: { type: 'boolean' },
      restoreStateQueryParam: { oneOf: [{ type: 'string' }, { type: 'array', items: { type: 'string' } }] },
      provideParentOverall: { type: 'boolean' },
      i18nOverride: i18nSchema,
      ...properties,
    },
    additionalProperties: false,
    required: ['id', 'firstId', 'pixels', 'template', ...required],
    definitions: {
      ...definitions,
      Theme: createMockedPollTheme(),
      TrueTheme: createTheme(),
      Pixel: createPixelSchema(),
    },
  } as const;
}

// eslint-disable-next-line @typescript-eslint/ban-types
type ListToRefMap<L extends readonly { name: string }[], Acc = {}> = L extends readonly [infer H, ...infer Rest]
  ? H extends { name: string }
    ? Rest extends { name: string }[]
      ? ListToRefMap<Rest, Acc & { [P in H['name']]: { $ref: `#/definitions/${H['name']}` } }>
      : Acc
    : Acc
  : Acc;
// eslint-disable-next-line @typescript-eslint/ban-types
type ListToSchemaMap<L extends readonly { name: string; schema: unknown }[], Acc = {}> = L extends readonly [
  infer H,
  ...infer Rest,
]
  ? H extends { name: string; schema: unknown }
    ? Rest extends { name: string; schema: unknown }[]
      ? ListToSchemaMap<Rest, Acc & { [P in H['name']]: H['schema'] }>
      : Acc
    : Acc
  : Acc;

export function createPollSchema<
  W extends readonly { name: string; schema: unknown }[],
  Q extends readonly { name: string; schema: unknown }[],
  F extends readonly { name: string; schema: unknown }[],
  S extends readonly { name: string; schema: unknown }[],
  P extends Record<string, JSONSchema7>,
>({
  welcomesSchema,
  questionsSchema,
  finalsSchema,
  screensSchema,
  rootSchemaProps,
}: {
  questionsSchema: Q;
  finalsSchema: F;
  screensSchema?: S;
  welcomesSchema?: W;
  rootSchemaProps?: P;
}) {
  const definitions = {
    QUESTION_TYPES: {
      type: 'object',
      properties: Object.fromEntries(
        questionsSchema.map(description => [description.name, toRef(description.name)] as const),
      ) as ListToRefMap<typeof questionsSchema>,
      required: questionsSchema.map(({ name }) => name) as Q[number]['name'][],
    },
    Group: createQuestionGroupSchema(),
    Set: createQuestionSetSchema(),
    QuestionDeck: createQuestionDeckSchema(),
    AnswerDeck: createAnswerDeckSchema(),
    Question: toUnion(questionsSchema.map(d => toRef(d.name as Q[number]['name']))),
    Final: toUnion(finalsSchema.map(d => toRef(d.name as F[number]['name']))),
    ...(screensSchema && { Screen: toUnion(screensSchema.map(d => toRef(d.name as S[number]['name']))) }),
    ...(welcomesSchema && { Welcome: toUnion(welcomesSchema.map(d => toRef(d.name as W[number]['name']))) }),
    ...(Object.fromEntries(
      [...(screensSchema ?? []), ...(welcomesSchema ?? []), ...questionsSchema, ...finalsSchema].map(
        ({ name, schema }) => [name, schema],
      ),
    ) as ListToSchemaMap<[...W, ...Q, ...F]>),
  };

  function toRef<N extends string>(name: N) {
    return { $ref: `#/definitions/${name}` } as const;
  }

  function toUnion<U extends unknown[]>(members: U) {
    return { oneOf: members } as const;
  }

  return createRootSchema(
    {
      ...rootSchemaProps,
      ...(welcomesSchema && { welcome: { type: 'array', items: { $ref: '#/definitions/Welcome' } } }),
      ...(screensSchema && {
        systemScreens: {
          type: 'array',
          items: { $ref: '#/definitions/SYSTEM_SCREEN' },
          description: 'System screen - объект, описывающий измененные системные экраны',
        },
      }),
      questions: { type: 'array', items: { $ref: '#/definitions/Question' } },
      groups: {
        type: 'array',
        description: '`Group` - объект описывающий несколько вопросов, показываемых одновременно (на одной странице)',
        items: { $ref: '#/definitions/Group' },
      },
      sets: {
        type: 'array',
        description:
          '`Set` - объект описывающий несколько логически связанных вопросов, показываемых последовательно (каждый на своей странице)',
        items: { $ref: '#/definitions/Set' },
      },
      questionDecks: {
        type: 'array',
        description:
          '`QuestionDeck` - объект описывающий несколько логически связанных вопросов, показываемых одновременно',
        items: { $ref: '#/definitions/QuestionDeck' },
      },
      hasPreinitializedState: {
        type: 'boolean',
        description: 'Указывает включено ли преиницилизированное состояние у опроса',
      },
      answerDecks: {
        type: 'array',
        description:
          '`AnswerDeck` - объект описывающий несколько логически связанных ответов, показываемых одновременно',
        items: { $ref: '#/definitions/AnswerDeck' },
      },
      finals: { type: 'array', items: { $ref: '#/definitions/Final' } },
    } as const,
    ['questions', 'groups', 'finals', 'questionDecks', 'answerDecks'] as const,
    {
      ...definitions,
      ...extractMixins(
        [...questionsSchema, ...(welcomesSchema ?? []), ...(screensSchema ?? []), ...finalsSchema].map(
          v => v.schema as SchemaWithDefinitions,
        ),
      ),
    },
  );
}

export function createQuestionSetSchema() {
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      data: {
        type: 'object',
        properties: {
          name: { type: 'string' },
          text: { type: 'string' },
          pageLimit: { type: 'number' },
          type: { enum: ['default', 'prototype'] },
          exclusiveAnswers: { type: 'array', items: exclusivePrototypeAnswerSchema },
          ...instantStatisticRequired,
          ...topLevelLogicRules,
          ...setRandomizePages,
        },
        additionalProperties: false,
        required: ['type'],
      },
    },
    additionalProperties: false,
    required: ['id', 'data'],
  } as const;
}

export function createQuestionGroupSchema() {
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      setId: { type: 'string', description: 'Идентификатор сета, к которому принадлежит группа' },
      data: {
        type: 'object',
        properties: {
          name: { type: 'string' },
          text: { type: 'string' },
          questionLimit: { type: 'number' },
          autoNext: { type: 'boolean' },
          autoCommit: { type: 'boolean' }, // Только для матриц
          systemButtonText: systemButtonText.properties.systemButtonText,
          ...questionRandomizeGroup,
          ...questionHasTimer,
          ...topLevelLogicRules,
          ...questionSequentialDisplay,
          ...instantStatisticRequired,
        },
        additionalProperties: false,
        required: [],
      },
    },
    additionalProperties: false,
    required: ['id', 'data'],
  } as const;
}

export function createQuestionDeckSchema() {
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      groupId: { type: 'string' },
      data: {
        type: 'object',
        properties: {
          name: { type: 'string' },
          text: { type: 'string' },
          type: { enum: ['matrix', 'card_sorting'] },
          questionLimit: { type: 'number' },
          randomizeDeck: { type: 'boolean' },
          randomizeDeckExcludedIds: {
            items: { type: 'string' },
            type: 'array',
          },
          ...topLevelLogicRules,
          ...instantStatisticRequired,
        },
        additionalProperties: false,
        required: ['type'],
      },
    },
    additionalProperties: false,
    required: ['id', 'groupId', 'data'],
  } as const;
}

export function createAnswerDeckSchema() {
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      questionId: { type: 'string' },
      data: {
        type: 'object',
        properties: {
          name: { type: 'string' },
          text: { type: 'string' },
          answerLimit: { type: 'number' },
          randomizeDeck: { type: 'boolean' },
          randomizeDeckExcludedIds: {
            items: { type: 'string' },
            type: 'array',
          },
          ...topLevelLogicRules,
          ...instantStatisticRequired,
        },
        additionalProperties: false,
        required: [],
      },
    },
    additionalProperties: false,
    required: ['id', 'questionId', 'data'],
  } as const;
}

export function createPixelSchema() {
  return {
    type: 'object',
    properties: {
      id: { type: 'number' },
      type: { enum: ['http', 'javascript'] },
      data: pixelData(),
      isSystem: { type: 'boolean' },
    },
    additionalProperties: false,
    required: ['id', 'type', 'data'],
  } as const;
}
export function createBackAnswerStateSchema() {
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      isUsed: { type: 'boolean' },
      createdAt: { type: 'number' },
      answersIds: { type: 'array', items: { type: 'string' } },
      answeredIds: { type: 'array', items: { type: 'string' } },
      answersOrder: { type: 'array', items: { type: 'number' } },
      answeredOthers: { type: 'string' },
      lastTouchedBy: { type: ['string', 'null'], enum: ['user', 'system'] },
    },
    additionalProperties: false,
    required: ['id', 'isUsed', 'lastTouchedBy'],
  } as const;
}

function pixelData() {
  return {
    type: 'object',
    properties: {
      domain: { type: 'string' },
      events: { type: 'array', items: pixelEvent() },
    },
    additionalProperties: false,
  } as const;
}

function pixelEvent() {
  return {
    type: 'object',
    properties: {
      name: { enum: Object.values(PIXEL_EVENTS) },
      pathname: { type: 'string' },
      params: { type: ['string', 'null'] },
      method: { type: 'string' },
      data: { type: 'string' },
      when: { type: ['string', 'null'] },
      domain: { type: 'string' }, // fixme!
      body: { type: ['string', 'null'] }, // fixme!
      contentType: { enum: ['application/x-www-form-urlencoded', 'application/json'] },
      timeout: { type: 'number' },
    },
    additionalProperties: false,
    required: ['name'],
  } as const;
}
